<template>
  <material-card
    v-resize="onResize"
    outlined
    class="fill-height d-flex linechart-root"
  >
    <chart-title :title="title" :unit="unitText(chartUnitPosition.TITLE)" :subtitle="subtitle" />
    <div class="legend-container">
      <div class="legend-item">
        <svg width="20" height="12">
          <line x1="0" y1="6" x2="20" y2="6" stroke="#202B34" stroke-width="2" />
        </svg>
        <span>{{legendPrimary}}</span>
      </div>
      <div class="legend-item">
        <svg width="20" height="12">
          <line x1="0" y1="6" x2="20" y2="6" stroke="#202B34" stroke-width="2" stroke-dasharray="5,3" stroke-opacity="0.5" />
        </svg>
        <span>{{legendSecondary}}</span>
      </div>
    </div>
    <div class="graph-legend-container">
      <div :id="id" class="graph-container">
        <svg
          v-if="initialized"
          :id="parentId"
          class="graph-svg"
          :style="{ 
            marginTop: '-' + stretchHeight + 'px', 
            height: 'calc(100% + ' + stretchHeight + 'px)',
            width: 'calc(100% + ' + stretchWidth + 'px)' 
          }"
        >
          <g>
            <g
              :id="`${id}-categories`"
              class="categories"
            />
            <g
              :id="`${id}-values`"
              class="values"
            />
            <!-- Horizontal Data -- No data text -->
            <g 
              v-if="hover.horizontal.hoveredLineIndex !== null && hasAllValuesUndefined(data[hover.horizontal.hoveredLineIndex])"
              :transform="`translate(${width/2}, ${height/2})`"
            >
              <text
                text-anchor="middle"
                dominant-baseline="middle"
                class="text-h6"
              >
                {{$t('views.charts.noData')}}
              </text>
            </g>
          </g>
          <g v-for="(lineData, lineIndex) in data" :key="parentId + '-line-' + lineIndex">
            <!-- Base line -->
            <line
              v-if="lineData.base"
              :x1="chartCategoriesBase(0)"
              :y1="chartValues(lineData.base)"
              :x2="chartCategoriesBase(data[0].values.length - 1)"
              :y2="chartValues(lineData.base)"
              :stroke="getColor(lineIndex)"
              stroke-width="2"
              stroke-dasharray="10,5"
              :stroke-opacity="hover.horizontal.hoveredLineIndex === lineIndex ? 1 : 0.5"
              :style="{
                opacity: getLineOpacity(lineIndex),
                transition: hover.transition
              }"
            />

            <!-- Values line -->
            <path
              :d="lineGenerator(lineData.values)"
              :stroke="getColor(lineIndex)"
              stroke-width="2"
              fill="none"
              :style="{
                opacity: getLineOpacity(lineIndex),
                transition: hover.transition
              }"
            />
            <!-- Horizontal Data -->
            <g
              v-for="(point, pointIndex) in lineData.values"
              :key="`point-group-${lineIndex}-${pointIndex}`"
              class="horizontal-data data-labels"
            >
              <!-- Circle -->
              <circle
                v-if="point !== null"
                :cx="chartCategories(pointIndex)"
                :cy="chartValues(point)"
                :r="spacings.dataLabels.circleDiameter"
                :stroke="getColor(lineIndex)"
                :stroke-width="spacings.dataLabels.circleStrokeWidth"
                :style="{
                  opacity: hover.horizontal.hoveredLineIndex === lineIndex ? 1 : 0,
                  transition: hover.transition
                }"
              />
              <!-- Value label -->
              <g v-if="hover.horizontal.hoveredLineIndex === lineIndex && point !== null">
                <rect
                  :x="chartCategories(pointIndex) - spacings.dataLabels.rectWidthHorizontalHover/2"
                  :width="spacings.dataLabels.rectWidthHorizontalHover"
                  :y="chartValues(point) - spacings.dataLabels.circleDiameter/2 - spacings.dataLabels.rectHeight - spacings.dataLabels.spaceBetweenCircle"
                  :height="spacings.dataLabels.rectHeight"
                  :rx="spacings.dataLabels.rectRadius"
                  :ry="spacings.dataLabels.rectRadius"
                />
                <text
                  :x="chartCategories(pointIndex)"
                  :y="chartValues(point) - spacings.dataLabels.circleDiameter/2 - 4 - spacings.dataLabels.spaceBetweenCircle"
                  text-anchor="middle"
                >
                  {{ Math.floor(point) }}
                </text>
              </g>
            </g>
          </g>
          <!-- Vertical Data (need to be after the chart to have a higher z-index) -->
          <g
            class="vertical-data data-labels"
            :style="{
              opacity: hover.vertical.showline ? 1 : 0,
              transition: hover.transition
            }"
            :transform="`translate(${hover.vertical.linePosition}, 0)`"  
          > 
            <line
              :y1="stretchHeight"
              :y2="height + stretchHeight - spacings.bottomMargin"
            />
            <text
              :y="height + stretchHeight - spacings.bottomMargin + 16"
              text-anchor="middle"
              class="category-label"
            >
              {{ getCategoryLabel(hover.vertical.dataIndex) }}
            </text>
            <g v-for="(lineData, lineIndex) in data" :key="`hover-value-${lineIndex}`">
              <g v-if="hover.vertical.dataIndex !== null && lineData.values[hover.vertical.dataIndex] !== null">
                <!-- Circle -->
                <circle
                  :cy="chartValues(lineData.values[hover.vertical.dataIndex])"
                  :r="spacings.dataLabels.circleDiameter"
                  :stroke="getColor(lineIndex)"
                  :stroke-width="spacings.dataLabels.circleStrokeWidth"
                />
                <!-- Value label -->
                <g :transform="`translate(0, ${chartValues(lineData.values[hover.vertical.dataIndex]) + (labelOffsets.get(`${lineIndex}-${hover.vertical.dataIndex}`) || 0)})`">
                  <rect
                    :x="isLabelOutOfBounds(hover.vertical.linePosition) ? 
                      -spacings.dataLabels.spaceBetweenCircle - spacings.dataLabels.rectWidthVerticalHover : 
                      spacings.dataLabels.spaceBetweenCircle"
                    :width="spacings.dataLabels.rectWidthVerticalHover"
                    :y="-spacings.dataLabels.rectHeight/2"
                    :height="spacings.dataLabels.rectHeight"
                    :rx="spacings.dataLabels.rectRadius"
                    :ry="spacings.dataLabels.rectRadius"
                    :fill="getColor(lineIndex, colorType.BACKGROUND)"
                    :stroke="getColor(lineIndex, colorType.BORDER)"
                  />
                  <text 
                    :x="isLabelOutOfBounds(hover.vertical.linePosition) ? 
                      -spacings.dataLabels.spaceBetweenCircle + spacings.dataLabels.rectPadding - spacings.dataLabels.rectWidthVerticalHover : 
                      spacings.dataLabels.spaceBetweenCircle + spacings.dataLabels.rectPadding"
                    y="4"
                  >
                    {{ Math.floor(lineData.values[hover.vertical.dataIndex]) + unit }}
                  </text>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
      <div class="legend-line-container">
        <div
          v-for="(lineData, lineIndex) in data"
          :key="'legend-right-' + lineIndex"
          :data-line-index="lineIndex"
          class="legend-line-item"
          :style="{
            opacity: getLineOpacity(lineIndex)
          }"
        >
          <span
            class="legend-line-preview"
            :style="{ backgroundColor: getColor(lineIndex) }"
          ></span>
          <span class="legend-line-name">
            <span class="text-body-1 color-text-primary">{{ lineData.name }}</span>
          </span>
        </div>
      </div>
    </div>
  </material-card>
</template>

<script>
import * as d3 from 'd3'
import resize from 'vue-resize-directive'
import { chartDirection, chartStatus, chartUnitPosition } from '@/utils/enum'
import ChartTitle from '@/components/DashViews/Charts/ChartTitle.vue'

export default {
  name: 'LineChart',
  components: {
    ChartTitle
  },
  directives: {
    resize,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
      default: '',
    },
    legendPrimary: {
      type: String,
      required: false,
      default: '',
    },
    legendSecondary: {
      type: String,
      required: false,
      default: '',
    },
    data: {
      type: [Array],
      required: true,
    },
    categoryLabels: {
      type: Array,
      required: false,
      default: () => [],
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
    unitPosition: {
      type: String,
      required: false,
      default: '',
    }
  },
  data: function () {
    return {
      initialized: false,
      isCreated: false,
      width: null,
      height: null,
      chartCategories: null,
      chartCategoriesBase: null,
      chartValues: null,
      chartDirection,
      chartStatus,
      chartUnitPosition,
      hover: {
        transition: 'opacity 0.2s ease',
        horizontal: {
          hoveredLineIndex: null,
          lowContrastLineOpacity: 0.2,
        },
        vertical: {
          showline: 0,
          linePosition: 0,
          dataIndex: null,
        }
      },
      spacings: {
        outerMargin: 8,
        bottomMargin: 20,
        leftAxisSpaceBetween: 8,
        leftAxisWidth: 42,
        dataLabels: {
          rectWidthHorizontalHover: 40,
          rectWidthVerticalHover: 56,
          rectHeight: 18,
          rectRadius: 2,
          rectPadding: 4,
          circleDiameter: 4,
          circleStrokeWidth: 2,
          spaceBetweenCircle: 10,
        }
      },
      colors: [
        {
          main: '#05A57D',
          background: '#EBFCF8',
          border: '#73BFAC'
        },
        {
          main: '#FF9400',
          background: '#FCF1E6',
          border: '#D69072'
        },
        {
          main: '#CF252D',
          background: '#FCE1E3',
          border: '#D68990'
        },
        {
          main: '#1792EB',
          background: '#D9EDFC',
          border: '#6999D1'
        },
        {
          main: '#4F29CC',
          background: '#ECE6FC',
          border: '#917CCC'
        }
      ],
      colorType: {
        MAIN: 'main',
        BACKGROUND: 'background',
        BORDER: 'border'
      }
    };
  },
  computed: {
    parentId() {
      return `${this.id}-svg`;
    },
    lineGenerator() {
      return d3.line()
        .x((_, i) => this.chartCategories(i))
        .y(d => this.chartValues(d))
        .curve(d3.curveCardinal.tension(0.4))
    },
    stretchHeight() {
      // Stretch the chart to prevent the dataLabels from being cut off
      return this.spacings.dataLabels.spaceBetweenCircle + this.spacings.dataLabels.rectHeight;
    },
    stretchWidth() {
      // Stretch the chart to prevent the dataLabels from being cut off
      return this.spacings.dataLabels.rectWidthHorizontalHover;
    },
    labelOffsets() {
      if (!this.data || !this.data[0]?.values || !this.chartValues) return new Map();
      
      const minSpacingBetweenLabels = 4;
      const labelHeight = this.spacings.dataLabels.rectHeight;
      const offsets = new Map();
      const getKey = (lineIndex, dataIndex) => `${lineIndex}-${dataIndex}`;

      // Process each data point (x position) separately
      for (let dataIndex = 0; dataIndex < this.data[0].values.length; dataIndex++) {
        let currentGroup = [];
        const groups = [];
        const requiredSpace = labelHeight + minSpacingBetweenLabels;
        const valuesAtXIndex = this.data.map((line, lineIndex) => ({
          y: this.chartValues(line.values[dataIndex]),
          lineIndex,
          dataIndex
        })).filter(item => item.y !== undefined);
        valuesAtXIndex.sort((a, b) => a.y - b.y);

        // Identify all groups of overlapping labels
        for (let i = 0; i < valuesAtXIndex.length; i++) {
          const current = valuesAtXIndex[i];
          currentGroup.push(current);

          const next = valuesAtXIndex[i + 1];
          if (!next || (next.y - current.y) >= requiredSpace) {
            groups.push([...currentGroup]);
            currentGroup = [];
          }
        }

        // Calculate offsets for each group
        groups.forEach(group => {
          if (group.length > 1) {
            const centerY = (group[0].y + group[group.length - 1].y) / 2;
            const totalSpaceNeeded = (group.length - 1) * requiredSpace;
            group.forEach((item, index) => {
              const offset = centerY - (totalSpaceNeeded / 2) + (index * requiredSpace) - item.y;
              offsets.set(getKey(item.lineIndex, dataIndex), offset);
            });
          }
        });
      }
      
      return offsets;
    },
  },
  watch: {
    data() {
      if (this.isCreated) this.onResize();
      this.$nextTick(() => {
        this.initVerticalHover();
        this.initHorizontalHover();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initVerticalHover();
      this.initHorizontalHover();
    })
  },
  methods: {
    hasAllValuesUndefined(lineData) {
      if (!lineData?.values?.length) return true;
      return lineData.values.every(value => value === null || value === undefined);
    },
    calcSize() {
      let parentBounding = d3.select(`#${this.id}`).node().getBoundingClientRect();
      this.width = parentBounding.width;
      this.height = parentBounding.height;
      
      const valuesLength = this.data[0].values?.length - 1;
      const valuesPositionLeft = this.spacings.leftAxisWidth
      const valuesPositionRight = this.width;
      this.chartCategories = d3
        .scaleLinear()
        .domain([0, valuesLength])
        .range([valuesPositionLeft + this.spacings.outerMargin, valuesPositionRight - this.spacings.outerMargin]);
      this.chartCategoriesBase = d3
        .scaleLinear()
        .domain([0, valuesLength])
        .range([valuesPositionLeft, valuesPositionRight]); // no outer margin, so the baseline extends to the edges of the chart

      const padding = 4;
      const valuesPositionTop = this.spacings.dataLabels.spaceBetweenCircle + this.spacings.dataLabels.rectHeight + padding;
      const valuesPositionBottom = this.height + this.stretchHeight - this.spacings.bottomMargin - padding;
      const allValues = this.data.flatMap(line => [...line.values, ...(line.base !== undefined ? [line.base] : [])]); // combinaison of values and base if they exist
      this.chartValues = d3
        .scaleLinear()
        .domain([Math.min(...allValues), Math.max(...allValues)])
        .nice()
        .range([valuesPositionBottom, valuesPositionTop])

      this.initialized = true;
    },
    renderAxes() {
      d3.select(`#${this.id}-values`)
        .attr('transform', `translate(${this.width}, 0)`)
        .call(
          d3.axisLeft(this.chartValues)
            .ticks(7)
            .tickFormat((d) => `${d}${this.unitText(chartUnitPosition.AXIS_LABEL)}`)
            .tickSizeInner(this.width - this.spacings.leftAxisWidth)
            .tickPadding(this.spacings.leftAxisSpaceBetween),
        )
        .call((g) => {
          g.select('.domain').remove()
        });
      this.isCreated = true;
    },
    onResize() {
      this.calcSize();
      setTimeout(this.renderAxes, 0);
    },
    getColor(index, type = this.colorType.MAIN) {
      return this.colors[index % this.colors.length][type];
    },
    unitText(position) {
      return this.unitPosition == position ? this.unit : '';
    },
    getLineOpacity(lineIndex) {
      const unhoveredChart = this.hover.horizontal.hoveredLineIndex === null;
      const lineHovered = this.hover.horizontal.hoveredLineIndex === lineIndex;
      return unhoveredChart || lineHovered ? 1 : this.hover.horizontal.lowContrastLineOpacity;
    },
    getCategoryLabel(dataIndex) {
      if (dataIndex === null || !this.data?.[0]?.values) return '';
      const valuesLength = this.data[0].values.length - 1;
      const label = this.categoryLabels[valuesLength - dataIndex];
      return label || '';
    },
    initHorizontalHover() {
      const legendItems = d3.selectAll(`.legend-line-item`);
      legendItems.on('mouseenter', (event) => {
        const lineIndex = parseInt(event.target.getAttribute('data-line-index'));
        this.hover.horizontal.hoveredLineIndex = lineIndex;
      })
      .on('mouseleave', () => {
        this.hover.horizontal.hoveredLineIndex = null;
      });
    },
    initVerticalHover() {
      const svg = d3.select(`#${this.parentId}`)
      svg.on('mousemove', (event) => {
        // Convert SVG coordinates to chart area coordinates
        const [mouseX] = d3.pointer(event)
        const containerX = mouseX - this.spacings.leftAxisWidth - this.spacings.outerMargin

        // Check if mouse is within the chart's data area:
        const containerWidth = this.width - this.spacings.leftAxisWidth - 2 * this.spacings.outerMargin;
        if (containerX >= 0 && containerX <= containerWidth) {
          const dataIndex = Math.round(this.chartCategories.invert(mouseX))
          
          // Make sure we have a valid data point at this index
          if (dataIndex >= 0 && dataIndex < this.data[0].values.length) {
            this.hover.vertical.showline = true;
            this.hover.vertical.linePosition = this.chartCategories(dataIndex);
            this.hover.vertical.dataIndex = dataIndex;
          }
        } else {
          this.hover.vertical.showline = false;
          this.hover.vertical.dataIndex = null;
        }
      })
      .on('mouseleave', () => {
        this.hover.vertical.showline = false;
        this.hover.vertical.dataIndex = null;
      })
    },
    isLabelOutOfBounds(xPosition) {
      const rightSideOfLabel = xPosition + this.spacings.dataLabels.rectWidthVerticalHover + this.spacings.dataLabels.spaceBetweenCircle;
      const rightSideOfChart = this.width - this.spacings.outerMargin
      return rightSideOfLabel > rightSideOfChart;
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/material-dashboard/variables.scss';

::v-deep(.v-card__text) {
  gap: $spacing-md;
  .linechart-root & {
    padding-bottom: 8px;
  }

  text {
    font-family: $body-font-family;
  }
  .values text {
    color: $text-secondary;
    font-size: $font-size-12;
  }
  .data-labels {
    pointer-events: none;
    text {
      color: $text-primary;
      font-size: 14px; //force to 14px
    }
    .category-label {
      color: $text-secondary;
      font-size: $font-size-12;
      font-weight: bold;
    }
    circle {
      fill: $white;
    }
    line {
      stroke: $grey-400;
      stroke-width: 1px;
    }

    &.horizontal-data rect {
      fill: $white;
      stroke: $grey-100;
    }
  }
}

::v-deep(.values) {
  line {
    opacity: 1;
    stroke-dasharray: 3 2;
    stroke: $divider-dark;
  }

  .tick:nth-child(even) {
    line {
      stroke: $divider-main;
    }
    text {
      display: none;
    }
  }
}

.linechart-root {
  width: 100%;
}

.graph-legend-container {
  display: flex;
  align-items: center;
  gap: 32px;
  height: 100%;
  padding-right: 8px;
}
.legend-container {
  display: flex;
  justify-content: flex-end;
  gap: 20px;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;

    span {
      font-family: $body-font-family;
      font-size: $font-size-14;
      line-height: 1em;
      color: $text-primary;

      //fixes alignment
      display: inline-block;
      padding-top: 1px; 
    }
  }
}
.legend-line-container {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding-bottom: 20px;

  .legend-line-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    padding: 4px 0;
  }

  .legend-line-preview {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
  }

  .legend-line-name {
    width: 100%;
    max-width: 96px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}
@media (min-width: 1263px) {
  .legend-line-container {
    .legend-line-name {
      max-width: 120px;
    }
  }
}

</style>


<!-- DOCUMENTATION --

LineChart.vue

  Description:
  This component renders a line chart with multiple lines, base lines and hover interactions.

  Props:
  - id: String                                        Id of the chart, used for d3
  - title: String                                     Title of the chart
  - subtitle: String          (Optional)              Subtitle of the chart
  - legendPrimary: String     (Optional)              Primary legend text for solid line
  - legendSecondary: String   (Optional)              Secondary legend text for dashed line
  - data: Array                                       Data to display in the chart, format:
                                                      [{ 
                                                        name: String,
                                                        base?: Number,         // Optional baseline value
                                                        values: Array<{
                                                          value: Number
                                                        }>
                                                      }]
  - categoryLabels: Array                             Labels for the categories
  - unit: String              (Optional)              Unit of the data values
  - unitPosition: String      (Optional)              Position of the unit (chartUnitPosition: title, axis-label)

  Features:
  - Multiple lines with different colors
  - Baseline support for each line (dashed)
  - Hover interactions:
    - Horizontal: Show all values for the line corresponding to the hovered legend item
    - Vertical: Show all values at a specific x position
      - Automatic label positioning to prevent overlap
      
-->